import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import { type NumberFormat, tickFormat } from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  layoutLegend,
} from "@/lib/tool-kit/charts/layout";
import { linePath } from "@/lib/tool-kit/charts/lines";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { estimateTextWidth, maxTextWidth } from "@/lib/tool-kit/charts/text";
import {
  clipPolygon,
  clipPolyline,
  type XY,
} from "@/lib/tool-kit/econ-micro/geometry";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * The graph microeconomics classes draw: price (or another value) up the vertical axis,
 * quantity along the horizontal, both starting at the origin. Curves are straight or
 * piecewise-straight lines in data coordinates; the chart clips them to the box, so a
 * caller can hand over a whole demand line from intercept to intercept.
 *
 * Callers choose `xMax` and `yMax` so that every dot lies inside the box; curves and
 * shaded polygons are clipped to it.
 */

export type LabelPosition = "ne" | "nw" | "se" | "sw" | "e" | "w" | "n" | "s";

export interface EconCurve {
  id: string;
  points: readonly XY[];
  color: string;
  dashed?: boolean;
  /** A short name drawn at the end of the curve: "D", "S", "MC". */
  label?: string;
  /** Draw the label at the first point instead of the last. */
  labelAtStart?: boolean;
  /** Draw the label below and to the right of this point of `points` (inside the box). */
  labelAtIndex?: number;
}

export interface EconShade {
  id: string;
  points: readonly XY[];
  color: string;
  opacity?: number;
}

export interface EconDot {
  id: string;
  x: number;
  y: number;
  label?: string;
  color?: string;
  position?: LabelPosition;
  /** Dashed lines from the dot to both axes. */
  guides?: boolean;
}

export interface EconLegendItem {
  label: string;
  color: string;
}

const LABEL_OFFSETS: Record<
  LabelPosition,
  { dx: number; dy: number; anchor: "start" | "middle" | "end" }
> = {
  ne: { dx: 8, dy: -8, anchor: "start" },
  nw: { dx: -8, dy: -8, anchor: "end" },
  se: { dx: 8, dy: 15, anchor: "start" },
  sw: { dx: -8, dy: 15, anchor: "end" },
  e: { dx: 9, dy: 4, anchor: "start" },
  w: { dx: -9, dy: 4, anchor: "end" },
  n: { dx: 0, dy: -10, anchor: "middle" },
  s: { dx: 0, dy: 17, anchor: "middle" },
};

/** Room between the plot and the image edge, for a curve label past the plot. */
const RIGHT_ROOM = 34;

const HALO = { stroke: CHART_INK.background, strokeWidth: 3 } as const;

function CurveLabel({
  curve,
  box,
  x,
  y,
  inside = false,
}: {
  inside?: boolean;
  curve: EconCurve;
  box: { top: number; right: number };
  x: number;
  y: number;
}) {
  if (!curve.label) {
    return null;
  }
  const onTop = y <= box.top + 1;
  const onRight = x >= box.right - 1;
  const width = estimateTextWidth(curve.label, {
    fontSize: CHART_FONT_SIZE.axisTitle,
    bold: true,
  });
  let place = { dx: 7, dy: 4, anchor: "start" as "start" | "middle" | "end" };
  if (inside) {
    place = { dx: 8, dy: 16, anchor: "start" };
  } else if (onTop && !onRight) {
    place = { dx: 0, dy: -8, anchor: "middle" };
  } else if (onRight && width > RIGHT_ROOM - 8) {
    // Too wide for the margin beside the plot: set it above the end, ending at the edge.
    place = { dx: 0, dy: -8, anchor: "end" };
  }
  return (
    <text
      fill={CHART_INK.text}
      fontSize={CHART_FONT_SIZE.axisTitle}
      fontWeight="bold"
      paintOrder="stroke"
      textAnchor={place.anchor}
      x={px(x + place.dx)}
      y={px(y + place.dy)}
      {...HALO}
    >
      {curve.label}
    </text>
  );
}

export function EconChart({
  id,
  label,
  description,
  xMax,
  yMax,
  xTitle,
  yTitle,
  title,
  curves = [],
  shades = [],
  dots = [],
  legend = [],
  fmt,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  label: string;
  description: string;
  /** Largest x any dot or shape reaches; the axis rounds up from here. */
  xMax: number;
  yMax: number;
  xTitle: string;
  yTitle: string;
  /** A heading drawn inside the image; leave out when the page already names it. */
  title?: string;
  curves?: readonly EconCurve[];
  shades?: readonly EconShade[];
  dots?: readonly EconDot[];
  legend?: readonly EconLegendItem[];
  fmt: NumberFormat;
}) {
  const xAxis = niceTicks(0, xMax, { includeZero: true });
  const yAxis = niceTicks(0, yMax, { includeZero: true });
  const yTick = tickFormat(fmt, yAxis.decimals);
  const xTick = tickFormat(fmt, xAxis.decimals);
  const legendLayout =
    legend.length > 0
      ? layoutLegend(
          legend.map((item) => item.label),
          CHART_WIDTH - 2 * CHART_PADDING
        )
      : null;
  const frame = cartesianFrame({
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title,
    xTitle,
    yTitle,
    legendHeight: legendLayout?.height ?? 0,
    leftLabelWidth: maxTextWidth(yAxis.ticks.map(yTick), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    rightPadding: RIGHT_ROOM,
    topPadding: 20,
  });
  const { plot } = frame;
  const sx = linearScale([xAxis.min, xAxis.max], [plot.x, plot.x + plot.width]);
  const sy = linearScale(
    [yAxis.min, yAxis.max],
    [plot.y + plot.height, plot.y]
  );
  const box = { xMax: xAxis.max, yMax: yAxis.max };
  const edges = { top: plot.y, right: plot.x + plot.width };
  const bottom = plot.y + plot.height;
  const pathOf = (points: readonly XY[]) =>
    points.map(([x, y]) => [sx(x), sy(y)] as const);

  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      {legendLayout ? (
        <SvgLegend
          keys={legend.map((item) => ({
            color: item.color,
            kind: "swatch" as const,
          }))}
          layout={legendLayout}
          x={CHART_PADDING}
          y={frame.legendTop}
        />
      ) : null}
      {shades.map((shade) => (
        <polygon
          fill={shade.color}
          fillOpacity={shade.opacity ?? 0.35}
          key={shade.id}
          points={pathOf(clipPolygon(shade.points, box))
            .map(([x, y]) => `${px(x)},${px(y)}`)
            .join(" ")}
          stroke="none"
        />
      ))}
      <ValueAxis
        format={yTick}
        grid={false}
        orient="left"
        plot={plot}
        scale={sy}
        ticks={yAxis.ticks}
      />
      <ValueAxis
        format={xTick}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={sx}
        ticks={xAxis.ticks}
      />
      {curves.map((curve) => {
        const pieces = clipPolyline(curve.points, box).map(pathOf);
        const piece = curve.labelAtStart ? pieces.at(0) : pieces.at(-1);
        const marked =
          curve.labelAtIndex === undefined
            ? undefined
            : curve.points[curve.labelAtIndex];
        let anchor = curve.labelAtStart ? piece?.at(0) : piece?.at(-1);
        if (marked) {
          anchor = [sx(marked[0]), sy(marked[1])];
        }
        return (
          <g key={curve.id}>
            {pieces.map((piecePoints) => (
              <path
                d={linePath(piecePoints)}
                fill="none"
                key={`${curve.id}-${piecePoints[0][0]}-${piecePoints[0][1]}`}
                stroke={curve.color}
                strokeDasharray={curve.dashed ? "6 4" : undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
              />
            ))}
            {anchor ? (
              <CurveLabel
                box={edges}
                curve={curve}
                inside={marked !== undefined}
                x={anchor[0]}
                y={anchor[1]}
              />
            ) : null}
          </g>
        );
      })}
      {dots.map((dot) => {
        const x = sx(dot.x);
        const y = sy(dot.y);
        const place = LABEL_OFFSETS[dot.position ?? "ne"];
        const color = dot.color ?? CHART_INK.text;
        return (
          <g key={dot.id}>
            {dot.guides ? (
              <g stroke={CHART_INK.axis} strokeDasharray="4 4" strokeWidth={1}>
                <line x1={px(plot.x)} x2={px(x)} y1={px(y)} y2={px(y)} />
                <line x1={px(x)} x2={px(x)} y1={px(y)} y2={px(bottom)} />
              </g>
            ) : null}
            <Marker fill={color} shape="circle" x={x} y={y} />
            {dot.label ? (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.tick}
                fontWeight="bold"
                paintOrder="stroke"
                textAnchor={place.anchor}
                x={px(x + place.dx)}
                y={px(y + place.dy)}
                {...HALO}
              >
                {dot.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </ChartSvg>
  );
}
